import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { ImportBoardTemplateTarget } from 'modules/core/domain/interfaces/supabase/overlay/auxiliary-types';
import { Template } from 'modules/core/domain/interfaces/supabase/overlay/entity';
import {
  BOARD_PROBLEM_TEXT,
  BOARD_TEMPLATE_IMPORT_REJECTION_TEXT,
  BoardTemplateImportRejection,
} from 'modules/template-editor/domain/consts/board-template-file-messages';
import { BOARD_TEMPLATE_FILE_SUFFIX, parseBoardTemplateFile, SkippedBoard } from 'modules/template-editor/domain/template-file';
import { useImportBoardTemplateMutation } from 'modules/template-editor/store/board-templates-api';
import React, { useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';
import { readFileAsText } from 'shared/utils/read-file-as-text';
import { useFilePicker } from 'ud-form/components/file-picker';

export const IMPORT_BOARD_TEMPLATE_INPUT_TEST_ID = 'import-board-template-input';

export interface UseImportBoardTemplateResult {
  isImporting: boolean;
  /** Открыть окно выбора файла — из обработчика клика. */
  pickFile: () => void;
  /**
   * Импорт выбранного файла. `null` — не запускался (уже идёт импорт, некуда импортировать) или файл отклонён
   * (причина — в тосте); иначе созданный шаблон.
   */
  importFile: (file: File) => Promise<Template | null>;
  /** Скрытый `<input type="file">` — отрендерить рядом с кнопкой. */
  fileInput: React.ReactElement;
}

/**
 * Импорт шаблона трансляции из `*.imgame-board-template.json` новым шаблоном (IMGM-1589): своим шаблоном
 * организации (`{ ownerId }`) или — супер-админ в каталоге — общим «готовым» без владельца (`{ shared: true }`).
 * Файл разбирает `parseBoardTemplateFile`; запись — одна транзакция `import_board_template`, итог в кэше списка и
 * каталога без второго запроса. Все исходы — тостами: отказ с причиной, успех с итоговым именем, пропущенные табло.
 *
 * `target: null` — импортировать некуда (организация ещё не загружена).
 */
export function useImportBoardTemplate(target: ImportBoardTemplateTarget | null): UseImportBoardTemplateResult {
  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const [importTemplate, { isLoading }] = useImportBoardTemplateMutation();
  // Ref, а не только `isLoading`: второй выбор файла до ре-рендера не должен проскочить.
  const busyRef = useRef(false);

  const reject = useCallback(
    (fileName: string, reason: BoardTemplateImportRejection) => {
      const cause = t(BOARD_TEMPLATE_IMPORT_REJECTION_TEXT[reason]);
      toast.error(t`Файл «${fileName}» не загружен: ${cause}`);
    },
    [t],
  );

  const describeSkipped = useCallback(
    (skipped: readonly SkippedBoard[]) =>
      skipped
        .map((entry) => {
          const position = entry.index + 1;
          const name = entry.name ?? t`табло №${position}`;
          const reason = t(BOARD_PROBLEM_TEXT[entry.reason]);
          return t`«${name}» — ${reason}`;
        })
        .join('; '),
    [t],
  );

  const importFile = useCallback(
    async (file: File): Promise<Template | null> => {
      if (!target || busyRef.current) return null;
      busyRef.current = true;
      try {
        let text: string;
        try {
          text = await readFileAsText(file);
        } catch {
          reject(file.name, 'read-failed');
          return null;
        }

        const parsed = parseBoardTemplateFile(text);
        if ('error' in parsed) {
          reject(file.name, parsed.error);
          return null;
        }
        // Функция отвергла бы пустой список табло — не шлём запрос, а говорим, почему.
        if (parsed.boards.length === 0) {
          reject(file.name, 'no-valid-boards');
          return null;
        }

        let created: Template;
        try {
          created = await importTemplate({ ...target, template: parsed.template, boards: parsed.boards }).unwrap();
        } catch (error) {
          toastApiError(error, msg`Не удалось импортировать шаблон`);
          return null;
        }

        const createdName = created.name;
        const sourceName = parsed.template.name;
        const boardsCount = parsed.boards.length;
        toast.success(
          createdName === sourceName
            ? t`Шаблон «${createdName}» импортирован, табло: ${boardsCount}`
            : t`Шаблон импортирован как «${createdName}» — название «${sourceName}» уже занято. Табло: ${boardsCount}`,
        );
        if (parsed.skipped.length > 0) {
          const skippedCount = parsed.skipped.length;
          const details = describeSkipped(parsed.skipped);
          toast.warning(t`Не импортированы табло (${skippedCount}): ${details}`);
        }
        return created;
      } finally {
        busyRef.current = false;
      }
    },
    [describeSkipped, importTemplate, target, reject, toastApiError, t],
  );

  const { open, input } = useFilePicker({
    // Сам формат проверяет парсер; фильтр окна — чтобы файл было легко найти.
    accept: `${BOARD_TEMPLATE_FILE_SUFFIX},.json,application/json`,
    onPick: importFile,
    testId: IMPORT_BOARD_TEMPLATE_INPUT_TEST_ID,
  });

  return { isImporting: isLoading, pickFile: open, importFile, fileInput: input };
}
